import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Badge } from '@shared/components/ui/badge'
import { Timeline, TimelineContent, TimelineItem, TimelineSeparator, TimelineDot, TimelineConnector } from '@shared/components/ui/timeline'
import { Clock, CheckCircle, XCircle, AlertCircle, DollarSign, RefreshCw } from 'lucide-react'

interface SubscriptionEvent {
  id: string
  type: 'created' | 'activated' | 'canceled' | 'resumed' | 'plan_changed' | 'payment' | 'refund'
  timestamp: string
  description: string
  amount?: number
  plan_name?: string
  billing_cycle?: string
  status?: string
}

interface SubscriptionTimelineProps {
  events: SubscriptionEvent[]
  loading?: boolean
}

export const SubscriptionTimeline: React.FC<SubscriptionTimelineProps> = ({ 
  events, 
  loading = false 
}) => {
  const getEventIcon = (type: string) => {
    switch (type) {
      case 'created':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'activated':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'canceled':
        return <XCircle className="h-4 w-4 text-red-600" />
      case 'resumed':
        return <RefreshCw className="h-4 w-4 text-blue-600" />
      case 'plan_changed':
        return <RefreshCw className="h-4 w-4 text-purple-600" />
      case 'payment':
        return <DollarSign className="h-4 w-4 text-green-600" />
      case 'refund':
        return <DollarSign className="h-4 w-4 text-red-600" />
      default:
        return <Clock className="h-4 w-4 text-gray-600" />
    }
  }

  const getEventColor = (type: string) => {
    switch (type) {
      case 'created':
      case 'activated':
      case 'payment':
        return 'bg-green-100 border-green-200'
      case 'canceled':
      case 'refund':
        return 'bg-red-100 border-red-200'
      case 'resumed':
      case 'plan_changed':
        return 'bg-blue-100 border-blue-200'
      default:
        return 'bg-gray-100 border-gray-200'
    }
  }

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatAmount = (amount?: number) => {
    if (!amount) return ''
    return `$${(amount / 100).toFixed(2)}`
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Subscription Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="text-muted-foreground">Loading timeline...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (events.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Subscription Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="text-muted-foreground">No timeline events found</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Subscription Timeline
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {events.map((event, index) => (
            <div key={event.id} className="flex items-start gap-4">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${getEventColor(event.type)}`}>
                {getEventIcon(event.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm">{event.description}</div>
                  <div className="text-xs text-muted-foreground">{formatDate(event.timestamp)}</div>
                </div>
                <div className="mt-1 space-y-1">
                  {event.amount && (
                    <div className="text-sm text-muted-foreground">
                      Amount: <span className="font-medium">{formatAmount(event.amount)}</span>
                    </div>
                  )}
                  {event.plan_name && (
                    <div className="text-sm text-muted-foreground">
                      Plan: <span className="font-medium">{event.plan_name}</span>
                      {event.billing_cycle && (
                        <span className="ml-1">({event.billing_cycle})</span>
                      )}
                    </div>
                  )}
                  {event.status && (
                    <Badge variant="outline" className="text-xs">
                      {event.status}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

